/**
 * CRA3D Resume Studio — Phase 5 read-only Preview + Export.
 *
 * Preview is FREE for every user with `resume_studio_access` (Mark's
 * try-before-buy). The export buttons are gated on the runtime status
 * flag `endpoints_available.export` — when the plan doesn't include
 * downloads, the buttons stay visible but disabled with an honest
 * "not included on your current plan" tooltip. No prices are shown.
 *
 * The traceability invariant is enforced server-side. A preview call
 * that hits an unapproved / dangling / AI-draft-only section returns
 * 400 with an itemised `reasons` list which we render as a clear
 * blocking notice — no broken file is ever handed to the browser.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { extractApiError } from "@/lib/api";
import { authenticatedDownload } from "@/lib/download";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import {
  FileText, ArrowLeft, Download, FileType, Lock,
  History, CheckCircle2, AlertTriangle,
} from "lucide-react";

const SECTION_LABEL = {
  summary:              "Summary",
  employment_history:   "Employment history",
  education:            "Education",
  skills_technical:     "Technical skills",
  skills_transferable:  "Transferable skills",
  projects:             "Projects",
  certifications:       "Certifications",
  contact:              "Contact",
  links:                "Links",
  volunteer:            "Volunteer",
  gaps_note:            "Notes",
};

function BlockedNotice({ message, reasons, onEdit }) {
  return (
    <div
      data-testid="resume-studio-preview-blocked"
      className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-5"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle
          className="h-5 w-5 flex-shrink-0 text-amber-500 mt-0.5"
          aria-hidden="true"
        />
        <div>
          <p className="text-sm font-medium text-fg-primary">
            {message || "This resume isn't ready to export yet."}
          </p>
          <p className="mt-1 text-xs text-fg-secondary">
            Every line on an exported resume must trace back to evidence
            you've approved. Fix the items below in the editor, then come
            back here.
          </p>
          {reasons?.length > 0 && (
            <ul
              data-testid="resume-studio-preview-blocked-reasons"
              className="mt-3 space-y-1 text-xs text-fg-secondary list-disc pl-5"
            >
              {reasons.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
          )}
          <Button
            size="sm"
            onClick={onEdit}
            data-testid="resume-studio-preview-back-to-editor-btn"
            className="mt-4 bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
          >
            Back to the editor
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function ResumeStudioPreview() {
  const { resumeId } = useParams();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [blocked, setBlocked] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(null); // "pdf" | "docx" | "txt"

  const load = async () => {
    setError(null);
    setBlocked(null);
    try {
      const [{ data: st }, { data: pv }] = await Promise.all([
        api.get("/resume-studio/status"),
        api.get(`/resume-studio/resumes/${resumeId}/preview`),
      ]);
      setStatus(st);
      setPreview(pv);
    } catch (err) {
      const s = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (s === 400 && typeof detail === "object" && detail?.reasons) {
        setBlocked({
          message: detail.message || "This resume isn't ready to export yet.",
          reasons: detail.reasons,
        });
        // Still load status so we can decide whether to show the disabled
        // export buttons or hide them entirely.
        try {
          const { data: st } = await api.get("/resume-studio/status");
          setStatus(st);
        } catch (_) { /* ignore */ }
      } else {
        setError(extractApiError(err, "Preview failed."));
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [resumeId]);

  const exportEnabled = status?.endpoints_available?.export === true;

  const doExport = async (fmt) => {
    setDownloading(fmt);
    try {
      const stem = (preview?.resume?.name || "resume")
        .toLowerCase().replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "") || "resume";
      const v = preview?.version?.version_number ?? "";
      const filename = `cra3d_resume_${stem}${v ? `_v${v}` : ""}.${fmt}`;
      const size = await authenticatedDownload(
        `/resume-studio/resumes/${resumeId}/export.${fmt}`,
        filename,
      );
      toast.success(
        `${fmt.toUpperCase()} ready (${(size / 1024).toFixed(1)} KB). ` +
        "Check your Downloads folder if it didn't open."
      );
    } catch (err) {
      // authenticatedDownload throws with a plain-language message; the
      // 402 / 400 paths surface here.
      toast.error(err?.message || "Export failed.");
      // If the server said 400 with reasons (traceability), reload the
      // preview so the blocking notice appears.
      if ((err?.message || "").toLowerCase().includes("trace")) {
        await load();
      }
    } finally {
      setDownloading(null);
    }
  };

  const sectionsWithContent = useMemo(
    () => (preview?.sections || []).filter(
      (s) => (s.approved_texts || []).length > 0),
    [preview],
  );

  if (loading) {
    return (
      <AppShell>
        <p className="mx-auto max-w-3xl px-4 py-16 text-center text-fg-secondary"
           data-testid="resume-studio-preview-loading">
          Loading preview…
        </p>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div
        data-testid="resume-studio-preview-page"
        className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12"
      >
        <button
          onClick={() => navigate(`/resume-studio/${resumeId}`)}
          data-testid="resume-studio-preview-back"
          className="mb-4 text-xs text-fg-secondary hover:text-fg-primary flex items-center gap-1"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
          Back to the editor
        </button>

        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-wider text-fg-secondary">
              Preview
            </p>
            <h1
              data-testid="resume-studio-preview-title"
              className="mt-1 text-2xl sm:text-3xl font-heading font-medium text-fg-primary flex items-center gap-2"
            >
              <FileText className="h-5 w-5" aria-hidden="true" />
              {preview?.resume?.name || "Resume"}
            </h1>
            {preview?.version?.version_number != null && (
              <p
                data-testid="resume-studio-preview-version-pill"
                className="mt-2 text-[11px] text-fg-muted"
              >
                Version {preview.version.version_number}
                {preview.version.id && ` · id ${preview.version.id.slice(0, 8)}`}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link to={`/resume-studio/${resumeId}/versions`}>
              <Button
                variant="ghost"
                size="sm"
                data-testid="resume-studio-preview-open-versions-btn"
                className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
              >
                <History className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
                Versions
              </Button>
            </Link>
          </div>
        </div>

        {error && (
          <Alert
            variant="destructive"
            data-testid="resume-studio-preview-error"
            className="mb-4"
          >
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {blocked && (
          <BlockedNotice
            message={blocked.message}
            reasons={blocked.reasons}
            onEdit={() => navigate(`/resume-studio/${resumeId}`)}
          />
        )}

        {!blocked && preview && (
          <>
            <section
              data-testid="resume-studio-preview-body"
              className="rounded-lg border border-border-subtle bg-bg-surface p-6 sm:p-8"
            >
              {sectionsWithContent.length === 0 ? (
                <p className="text-sm text-fg-secondary text-center py-6"
                   data-testid="resume-studio-preview-empty">
                  This resume doesn't have any approved content yet.
                  Add and approve at least one entry to preview.
                </p>
              ) : (
                <div className="space-y-6">
                  {sectionsWithContent.map((s) => (
                    <div
                      key={s.section_kind}
                      data-testid={`resume-studio-preview-section-${s.section_kind}`}
                    >
                      <h2 className="text-xs uppercase tracking-widest text-fg-secondary font-medium">
                        {SECTION_LABEL[s.section_kind] || s.section_kind}
                      </h2>
                      <ul className="mt-2 space-y-2 text-sm text-fg-primary leading-relaxed">
                        {s.approved_texts.map((t, i) => (
                          <li key={i} className="pl-4 relative">
                            <span
                              className="absolute left-0 top-2 h-1.5 w-1.5 rounded-full bg-accent-warm"
                              aria-hidden="true"
                            />
                            <span
                              data-testid={`resume-studio-preview-section-${s.section_kind}-line-${i}`}
                            >
                              {t}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-8 pt-4 border-t border-border-subtle flex items-center gap-2 text-[11px] text-fg-muted"
                   data-testid="resume-studio-preview-trailer">
                <CheckCircle2 className="h-3 w-3 text-progress-teal" aria-hidden="true" />
                <span>
                  Every line above traces to evidence you approved.
                </span>
              </div>
            </section>

            <div
              data-testid="resume-studio-preview-export-panel"
              className="mt-6 rounded-lg border border-border-subtle bg-bg-surface p-5"
            >
              <p className="text-sm font-medium text-fg-primary flex items-center gap-2">
                <Download className="h-4 w-4" aria-hidden="true" />
                Download this version
              </p>
              {exportEnabled ? (
                <p className="mt-1 text-xs text-fg-secondary">
                  ATS-safe formats. Each file carries a version trailer so
                  you always know which snapshot of your resume you sent.
                </p>
              ) : (
                <p
                  data-testid="resume-studio-preview-export-honest-copy"
                  className="mt-1 text-xs text-fg-secondary"
                >
                  Downloading a finished resume isn't included on your current
                  access plan. Previewing and continuing to edit stay free.
                </p>
              )}
              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  onClick={() => doExport("pdf")}
                  disabled={!exportEnabled || downloading != null || sectionsWithContent.length === 0}
                  data-testid="resume-studio-preview-export-pdf-btn"
                  className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover disabled:opacity-60"
                  title={!exportEnabled
                    ? "Downloads aren't included on your current plan."
                    : "Download PDF"}
                >
                  {!exportEnabled && <Lock className="h-3.5 w-3.5 mr-2" aria-hidden="true" />}
                  {exportEnabled && <FileType className="h-3.5 w-3.5 mr-2" aria-hidden="true" />}
                  {downloading === "pdf" ? "Preparing…" : "PDF"}
                </Button>
                <Button
                  onClick={() => doExport("docx")}
                  disabled={!exportEnabled || downloading != null || sectionsWithContent.length === 0}
                  data-testid="resume-studio-preview-export-docx-btn"
                  variant="outline"
                  className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover disabled:opacity-60"
                  title={!exportEnabled
                    ? "Downloads aren't included on your current plan."
                    : "Download Word (.docx)"}
                >
                  {!exportEnabled && <Lock className="h-3.5 w-3.5 mr-2" aria-hidden="true" />}
                  {exportEnabled && <FileType className="h-3.5 w-3.5 mr-2" aria-hidden="true" />}
                  {downloading === "docx" ? "Preparing…" : "Word (.docx)"}
                </Button>
                <Button
                  onClick={() => doExport("txt")}
                  disabled={!exportEnabled || downloading != null || sectionsWithContent.length === 0}
                  data-testid="resume-studio-preview-export-txt-btn"
                  variant="outline"
                  className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover disabled:opacity-60"
                  title={!exportEnabled
                    ? "Downloads aren't included on your current plan."
                    : "Download plain text (.txt)"}
                >
                  {!exportEnabled && <Lock className="h-3.5 w-3.5 mr-2" aria-hidden="true" />}
                  {exportEnabled && <FileType className="h-3.5 w-3.5 mr-2" aria-hidden="true" />}
                  {downloading === "txt" ? "Preparing…" : "Plain text (.txt)"}
                </Button>
              </div>
              {!exportEnabled && (
                <p className="mt-3 text-[11px] text-fg-muted italic"
                   data-testid="resume-studio-preview-export-plans-note">
                  Payments aren't active in this phase — no purchase path is
                  available today. Ask an administrator if you need downloads
                  enabled on your account, or{" "}
                  <Link
                    to="/plans?gated_from=Resume%20Studio%20downloads"
                    data-testid="resume-studio-preview-view-plans-link"
                    className="underline decoration-accent-warm/40 hover:decoration-accent-warm"
                  >
                    see what each level includes
                  </Link>.
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}
